import { USER_TYPES } from 'constants';
import { DeleteModalInterface } from 'constants/interfaces';
import { deleteModalInitialValues, deleteModalSchema } from 'constants/schemaValidations';
import { useFormik } from 'formik';
import useTranslate from 'hooks/useTranslate';
import React, { ReactNode, useState } from 'react';
import { Button, Form, Modal, Stack, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
	logoutZIM,
	useDeactivateMutation,
	useDeleteMutation,
	useGetReportReasonsQuery,
	useLogoutMutation,
} from 'services';
import useAuthStore, { resetState, setUserInfo } from 'store/auth';
import useCommonStore from 'store/common';
import { disconnect } from 'store/socket';
import { resetFeeds } from 'store/feeds';
import { resetAllStores } from 'store/resetStore';
import { clearStorageExceptPrefix } from 'utils';

const DeleteModal: React.FC<any> = () => {
	const { modalConfig = {}, hideCommonModal }: any = useCommonStore((state) => state);
	const navigate = useNavigate();
	const { translate } = useTranslate();
	const { user }: Record<string, any> = useAuthStore((state) => state.userInfo ?? {});
	const { mutateAsync: logoutMutation, isPending } = useLogoutMutation();
	const { mutateAsync: deleteMutation } = useDeleteMutation();
	const { mutateAsync: deactivateMutation } = useDeactivateMutation();
	const { data: { deleteDeactivateReasons = [] } = {} } = useGetReportReasonsQuery();

	const formik = useFormik({
		initialValues: { reason: '', description: '' },
		validationSchema: deleteModalSchema(translate),
		onSubmit: async (values) => {
			try {
				if (modalConfig?.tabKey === 'delete-account') {
					const res = await deleteMutation({ ...values });
					if (res?.success) {
						toast.success(res?.message);
						formik.resetForm();
						handleLogout();
					}
				} else if (modalConfig?.tabKey === 'deactivate-account') {
					const res = await deactivateMutation({ ...values });
					if (res?.success) {
						toast.success(res?.message);
						formik.resetForm();
						handleLogout();
					}
				}
			} catch (error) {
				console.error('Operation failed:', error);
				toast.error(translate('ToastMessage.anUnexpectedErrorOccurred'));
			}
		},
	});

	const renderError = <T extends keyof DeleteModalInterface>(field: T) =>
		formik.touched[field] && formik.errors[field] ? (
			<span className="text-danger f-error">{formik.errors[field] as ReactNode}</span>
		) : null;

	// Handle the radio button change
	const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;
		formik.setFieldValue('reason', value); // Update Formik's value for reason
		if (value !== 'other') {
			formik.setFieldValue('description', ''); // Clear description when not "Other"
		}
	};

	// Handle description input change
	const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		formik.setFieldValue('description', e.target.value); // Update Formik's value for description
	};

	const handleLogout = () => {
		logoutMutation()
			.then((res: any) => {
				resetFeeds();
				clearStorageExceptPrefix(sessionStorage, ['uc_']);
				clearStorageExceptPrefix(localStorage, ['uc_', 'i18nextLng']);
				resetAllStores();
				if (res?.statusCode === 401) {
					logoutZIM();
					disconnect();
					resetState();
				}
				if (res?.success) {
					logoutZIM();
					disconnect();
					setUserInfo({});
					toast.success(res?.message);
				}
				navigate(
					`/login?userType=${user?.userType === USER_TYPES.COMPANY ? USER_TYPES.COMPANY : USER_TYPES.SEEKER}`,
				);
			})
			.catch((error) => {
				console.error('Logout mutation failed:', error);
				toast.error(translate('Common.logoutFailed'));
			});
	};

	return (
		<Modal.Body>
			<Button
				className="close-btn rounded-circle border-0 fw-bold position-absolute"
				onClick={() => hideCommonModal()}
			>
				<i className="ri-close-line"></i>
			</Button>
			<Form className="edit-form" onSubmit={formik.handleSubmit}>
				<Stack className="gap-4">
					<h4 className="fw-bold mb-0">{translate('Modal.sureYouWant')}</h4>
					<Row>
						<Col md="12">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('Modal.reason')}</Form.Label>
								{deleteDeactivateReasons.map((reason) => (
									<div key={reason.value}>
										<Form.Check
											inline
											key={reason}
											label={`${reason.title}`}
											name="reason"
											type="radio"
											value={reason.value}
											checked={formik.values.reason === reason.value}
											onChange={handleRadioChange}
											className="mb-2 mb-xxl-3"
										/>
									</div>
								))}
								{renderError('reason')}
							</Form.Group>
						</Col>

						<Col md="12">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('Modal.description')}</Form.Label>
								<Form.Control
									type="text"
									placeholder={translate('Modal.writeReason')}
									name="description"
									className="rounded-pill"
									value={formik.values.description}
									onChange={handleDescriptionChange}
									autoComplete="off"
								/>
								{renderError('description')}
							</Form.Group>
						</Col>
					</Row>

					<div className="edit-modal-actions d-flex gap-4">
						<Button className="filled-btn rounded-pill" type="submit" disabled={isPending}>
							{translate('Modal.submit')}
						</Button>
					</div>
				</Stack>
			</Form>
		</Modal.Body>
	);
};

export default DeleteModal;
